import { useState, useEffect } from "react";
import { useRuntimeConstants } from "../../../context/runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../../../context/featuresReady/FeaturesReadyContext";
import getReturnNotesSummary, {
  ReturnNotesSummary,
} from "../../../api/returnNotesSummary";
import { getFeature } from "../../../utils/features";

type RuntimeConstantsShape = {
  features: {
    returnNotesSummaryEnabled: boolean;
  };
};

type FeaturesReadyShape = {
  areFeaturesReady: boolean;
};

const isValidReturnNotesSummary = (
  data: ReturnNotesSummary | null,
): data is ReturnNotesSummary => {
  const hasValue = (value: string | undefined) =>
    typeof value === "string" && value.trim().length > 0;

  return Boolean(data && hasValue(data.summary) && hasValue(data.disclaimer));
};

const useReturnNotesSummary = (): ReturnNotesSummary | false => {
  const runtimeConstants = useRuntimeConstants() as RuntimeConstantsShape;
  const {
    features: { returnNotesSummaryEnabled },
  } = runtimeConstants;

  const [returnNotesSummary, setReturnNotesSummary] =
    useState<ReturnNotesSummary | null>(null);

  useEffect(() => {
    if (!returnNotesSummaryEnabled || returnNotesSummary !== null) {
      return;
    }

    let isMounted = true;

    const fetchReturnNotesSummary = async () => {
      try {
        const data = await getReturnNotesSummary();
        if (isMounted) {
          setReturnNotesSummary(isValidReturnNotesSummary(data) ? data : null);
        }
      } catch {
        if (isMounted) {
          setReturnNotesSummary(null);
        }
      }
    };

    fetchReturnNotesSummary();

    return () => {
      isMounted = false;
    };
  }, [returnNotesSummaryEnabled, returnNotesSummary]);

  const { areFeaturesReady } = useFeaturesReady() as FeaturesReadyShape;

  const [isFeatureFlagEnabled, setIsFeatureFlagEnabled] = useState(false);

  useEffect(() => {
    if (!areFeaturesReady || !isValidReturnNotesSummary(returnNotesSummary)) {
      return;
    }

    setIsFeatureFlagEnabled(
      getFeature("pdp-return-notes-summaries").isEnabled(),
    );
  }, [areFeaturesReady, returnNotesSummary]);

  return (
    returnNotesSummaryEnabled &&
    isFeatureFlagEnabled &&
    isValidReturnNotesSummary(returnNotesSummary) &&
    returnNotesSummary
  );
};

export default useReturnNotesSummary;
